/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import dompurify from 'dompurify';
import _ from 'lodash';
import { normalize } from 'normalizr';

import { fetchEntities } from '@ibm-watson/shared/modules/actions/store/entities/actions';
import { FIELDS as WEBCHAT_FIELDS } from '@ibm-watson/shared/modules/channels/components/web-chat/schema';
import { updateIntegration } from '@ibm-watson/shared/modules/channels/store/channels/actions';
import { selectDraftWebChat } from '@ibm-watson/shared/modules/channels/store/channels/webchat-selectors';
import { getSkillStatus } from '@ibm-watson/shared/modules/ia/api/skills';
import { updateTaskCompletionStatus } from '@ibm-watson/shared/modules/ia/hooks/useTaskListHelpers';
import {
  isActionsPrivacyEnabled,
  selectIsAccessUpdatesEnabled
} from '@ibm-watson/shared/modules/ia/store/features/selectors';
import { isCacheExpired } from '@ibm-watson/shared/modules/ia/store/helper';
import { selectIsNewIa } from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import * as messagesActions from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { ToastStatus } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import * as operations from '@ibm-watson/shared/modules/ia/store/operations';
import {
  getActionSkillId,
  getAssistantId
} from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { selectSkillLanguage } from '@ibm-watson/shared/modules/ia/store/skills/core-selectors';
import { selectLaunchModeSettings } from '@ibm-watson/shared/modules/ia/store/skills/selectors';
import { getErrorString } from '@ibm-watson/shared/modules/ia/util/errorHandler';
import {
  createdObjectEvents,
  deletedObjectEvents,
  ranProcessEvents,
  updatedObjectEvents
} from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import {
  Action,
  DATA_TYPE,
  InputValidationHandler,
  ResolverType,
  Step,
  StepQuestion,
  SlotFillingExample,
  ActionSkillTypes
} from '../../../../ts/action';
import { TaskListKey } from '../../../../ts/preferences';
import { LaunchMode } from '../../../../ts/skill';
import { CreateAsyncThunk, Entity } from '../../../../ts/state';
import { selectDraftEnvironment } from '../../../ia/store/environments/selectors';
import * as actionsAPI from '../../api/actions';
import * as counterExampleApi from '../../api/counter-examples';
import { getIntent } from '../../api/intents';
import { sendTransactions } from '../../api/transactions';
import {
  ActionType,
  AIGuidedActionStep
} from '../../components/lists/constants';
import * as actionUtil from '../../util/actionUtil';
import { Transaction, TransactionOperation } from '../../util/transactionUtil';
import { getResultVariableId } from '../../util/variableUtil';
import { addBoosts, removeAllBoosts } from '../boosts/actions';
import { listDataTypes } from '../data-types/actions';
import { removeDataType, saveDataType } from '../data-types/ducks/update';
import {
  selectCustomDataTypeById,
  selectCustomDataTypeIds
} from '../data-types/selectors';
import {
  getById,
  getById as selectEntityById,
  getOrderedIds
} from '../entities/selectors';
import { fetchIntents } from '../intents/actions';
import * as modelsActionTypes from '../models/actions/actionTypes';
import { listVariables } from '../variables/actions';
import { getVariables } from '../variables/selectors';
import { pollSkillTrainingStatus } from '../workspacestate/actions';
import * as actionTypes from './actionTypes';
import {
  actionSchema,
  actionsSchema,
  counterExamplesSchema,
  intentSchema,
  subActionSchema
} from './schema';
import {
  assemblePayloadToSave,
  getAction as getActionSelector,
  getCurrentAction,
  getCurrentActionId,
  getOrderedSteps,
  getStepById,
  getStepEntityType,
  isAutoSaveEnabled,
  isStepVariablePrivate
} from './selectors';

// Open Preview web chat with debug mode enabled
export const openPreviewDebugMode = () => ({
  type: actionTypes.OPEN_PREVIEW_DEBUG_MODE
});

export const closePreviewDebugMode = () => ({
  type: actionTypes.CLOSE_PREVIEW_DEBUG_MODE
});

// --- listActions ---

export const listActionsStart = skillId => ({
  type: actionTypes.LIST_ACTIONS_START,
  skillId
});

export const listActionsSuccess = (skillId, response) => ({
  type: actionTypes.LIST_ACTIONS_SUCCESS,
  response,
  skillId,
  payload: {
    operation: operations.LIST
  }
});

export const listActionsError = (response, error) => ({
  type: actionTypes.LIST_ACTIONS_ERROR,
  response,
  error
});

export const listActions =
  (skillId, reactivateSession?) => async (dispatch, getState) => {
    // Avoid concurrent requests
    if (getState()?.actions.loadingActions) {
      return Promise.resolve({});
    }

    dispatch(listActionsStart(skillId));
    try {
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listActions
      const assistantId = getAssistantId(getState());
      const isNewIA = selectIsNewIa(getState());
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(getState());
      const response = await actionsAPI.listActions(
        skillId,
        undefined,
        reactivateSession,
        useNewIAUpdatedAccess,
        assistantId
      );
      // set default titles for old default actions.
      // Once we release the action skill or we don't have any action skill older than Aug
      // we can delete these codes.
      const currentActionId = getCurrentActionId(getState());
      response.actions.forEach(action => {
        if (action.action === 'anything_else') {
          action.title = actionUtil.NO_MATCHES_LABEL;
        }
        if (action.action === 'welcome') {
          action.title = 'Greet customer';
        }
        if (action.action === 'fallback') {
          action.title = 'Fallback';
        }
        if (currentActionId && action.action === currentActionId) {
          const orderedSteps = getOrderedSteps(getState());
          if (orderedSteps) {
            const stepsArray = Object.keys(orderedSteps).map(
              stepId => orderedSteps[stepId]
            );
            action.steps = stepsArray;
          }
        }
      });
      const normalizedResponse = normalize(response.actions, actionsSchema);
      if (normalizedResponse.result && normalizedResponse.result.length === 0) {
        //normalizr does not reset the entity to be undefined if an
        //empty list is returned, further discussion here:
        //https://github.com/paularmstrong/normalizr/issues/290#
        normalizedResponse.entities = { actions: {} };
      }
      dispatch(listActionsSuccess(skillId, normalizedResponse));
      return response;
    } catch (err) {
      const emptyNormalizedResponse = { entities: { actions: {} } };
      const message = getErrorString(err);
      dispatch(listActionsError(emptyNormalizedResponse, message));
      dispatch(
        messagesActions.addMessage({
          message,
          status: ToastStatus.ERROR,
          transactionId: err.transactionId
        })
      );
      throw err;
    }
  };

export const listActionsIfRequired =
  (skillId, reactivateSession?) => async (dispatch, getState) => {
    const {
      skillId: cachedSkillId,
      isDirty,
      listFetched
    } = getState()?.actions;

    if (skillId === cachedSkillId && !isDirty && !isCacheExpired(listFetched)) {
      return Promise.resolve({});
    }

    return dispatch(listActions(skillId, reactivateSession));
  };

const whichActionType = (actionType: string): string => {
  const type =
    actionType === ActionSkillTypes.AI_GUIDED
      ? 'AI guided action'
      : actionType === ActionSkillTypes.STANDARD
      ? 'Custom built action'
      : 'Skill based action';
  return type;
};

export const addLaunchMode = launchMode => ({
  type: actionTypes.ADD_LAUNCH_MODE,
  launchMode
});

export const getRelatedIntentSuccess = response => {
  return {
    type: actionTypes.GET_RELATED_INTENT_SUCCESS,
    examples: Object.keys(response.entities.examples ?? {}),
    payload: {
      operation: operations.GET,
      response
    }
  };
};

export const getRelatedIntentError = () => {
  return {
    type: actionTypes.GET_RELATED_INTENT_ERROR,
    response: {
      entities: {
        intents: {},
        examples: {}
      }
    },
    payload: {
      operation: operations.RESET
    }
  };
};

export const updateWebChatHomeScreenButtons = (
  state,
  dispatch,
  title,
  oldTitle?,
  deleteLink?
) => {
  const newIA = selectIsNewIa(state);
  const draftEnvironment = selectDraftEnvironment(state);
  const draftEnvironmentId = draftEnvironment?.environment_id;
  if (newIA && draftEnvironmentId) {
    const webchatIntegration = selectDraftWebChat(state);
    if (webchatIntegration) {
      const integration = _.cloneDeep(webchatIntegration);
      const buttons = _.get(integration, [
        'settings',
        ...WEBCHAT_FIELDS.HOME_SCREEN.STARTERS.BUTTONS.split('.')
      ]);
      if (buttons) {
        if (deleteLink && title) {
          const examples = [
            'Example: Find nearby location',
            'Example: Check account balance',
            'Example: See how I can help'
          ];
          const button = buttons.find(
            button => button.label.toLowerCase() === title.toLowerCase()
          );
          if (button) {
            //a button has the same label as the action being deleted - replace the title of the button with an example button label.
            for (const example of examples) {
              if (!buttons.some(button => button.label === example)) {
                //none of the buttons are using this pre-built example text - let's use this example text!
                button.label = example;
                dispatch(
                  updateIntegration({
                    assistantId: draftEnvironment.assistant_id,
                    environmentId: draftEnvironmentId,
                    integration
                  })
                );
                break;
              }
            }
          }
        } else if (title && title.length > 1 && title.length < 35) {
          if (oldTitle) {
            const button = buttons.find(
              button => button?.label?.toLowerCase() === oldTitle.toLowerCase()
            );
            if (button) {
              button.label = title;
              dispatch(
                updateIntegration({
                  assistantId: draftEnvironment.assistant_id,
                  environmentId: draftEnvironment.environment_id,
                  integration
                })
              );
            }
          } else if (!oldTitle) {
            const prefix = 'Example: ';
            if (buttons.some(button => button.label?.indexOf(prefix) !== -1)) {
              //the button has 'Example:' in the label, this suggests it is a default label, let's use the action's title
              //as the label for the button!
              const buttonToUpdate = _.find(
                _.get(integration, [
                  'settings',
                  ...WEBCHAT_FIELDS.HOME_SCREEN.STARTERS.BUTTONS.split('.')
                ]),
                button => button.label?.indexOf(prefix) !== -1,
                0
              );
              buttonToUpdate.label = title;
              dispatch(
                updateIntegration({
                  assistantId: draftEnvironment.assistant_id,
                  environmentId: draftEnvironment.environment_id,
                  integration
                })
              );
            }
          }
        }
      }
    }
  }
};

// --- deleteAction ---

export const deleteActionStart = (skillId, actionId) => ({
  type: actionTypes.DELETE_ACTION_START,
  actionId,
  skillId
});

export const deleteActionSuccess = response => ({
  type: actionTypes.DELETE_ACTION_SUCCESS,
  payload: {
    operation: operations.DELETE,
    response
  }
});

export const deleteActionError = error => ({
  type: actionTypes.DELETE_ACTION_ERROR,
  error
});

export const deleteAction =
  (skillId, actionId) => async (dispatch, getState) => {
    dispatch(deleteActionStart(skillId, actionId));
    try {
      const params = {
        delete_associated_intents_entities: true
      };
      const title = _.get(getState(), `models.actions.${actionId}.title`);
      const actionSkillType = _.get(
        getState(),
        `models.actions.${actionId}.type`
      );

      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with only selectIsNewIa on call to deleteAction
      const useNewIAUpdatedAccess =
        selectIsNewIa(getState()) && selectIsAccessUpdatesEnabled(getState());
      const assistantId = getAssistantId(getState());

      await actionsAPI.deleteAction(
        skillId,
        actionId,
        params,
        useNewIAUpdatedAccess,
        assistantId
      );
      dispatch(
        deleteActionSuccess(normalize({ action: actionId }, actionSchema))
      );
      if (title) {
        updateWebChatHomeScreenButtons(getState(), dispatch, title, null, true);
      }
      dispatch(listActions(skillId));
      dispatch(pollSkillTrainingStatus());

      // track successful action deletion
      deletedObjectEvents.action({
        object: skillId ? `${skillId}--${actionId}` : actionId,
        resultValue: 'success',
        successFlag: true,
        objectType: whichActionType(actionSkillType)
      });
    } catch (error) {
      const message = getErrorString(error);
      dispatch(
        messagesActions.addMessage({
          message,
          status: ToastStatus.ERROR,
          transactionId: error.transactionId
        })
      );
      dispatch(deleteActionError(error));
      throw error;
    }
  };

const triggerDeleteObjectEvent = (event, skillId) => {
  if (event) {
    switch (event.type) {
      case 'action':
        deletedObjectEvents.action({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true,
          objectType: whichActionType(event?.actionType)
        });
        break;
      case 'intent':
        deletedObjectEvents.intent({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true
        });
        break;
      case 'entity':
        deletedObjectEvents.entity({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true
        });
        break;
      case 'variable':
        deletedObjectEvents.globalVariable({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true
        });
        break;
      default:
        break;
    }
  }
};

const triggerUpdateObjectEvent = (event, skillId) => {
  if (event) {
    switch (event.type) {
      case 'action':
        updatedObjectEvents.action({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true,
          objectType: whichActionType(event?.actionType)
        });
        break;
      case 'intent':
        updatedObjectEvents.intent({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true
        });
        break;
      case 'entity':
        updatedObjectEvents.entity({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true
        });
        break;
      case 'variable':
        updatedObjectEvents.globalVariable({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true
        });
        break;
      case 'dynamicSubaction':
        updatedObjectEvents.dynamicSubaction({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true
        });
        break;
      default:
        break;
    }
  }
};

const triggerCreateObjectEvent = (event, skillId) => {
  if (event) {
    switch (event.type) {
      case 'action':
        createdObjectEvents.action({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true,
          objectType: whichActionType(event?.actionType)
        });
        break;
      case 'intent':
        createdObjectEvents.intent({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true
        });
        break;
      case 'entity':
        createdObjectEvents.entity({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true
        });
        break;
      case 'variable':
        createdObjectEvents.globalVariable({
          object: skillId ? `${skillId}--${event.id}` : event.id,
          resultValue: 'success',
          successFlag: true
        });
        break;
      default:
        break;
    }
  }
};

// -- setActionDescription ---
export const setActionDescriptionStart = response => ({
  type: actionTypes.UPDATE_ACTION_DESCRIPTION_START,
  response,
  payload: {
    operation: operations.UPDATE
  }
});

export const setActionDescriptionSuccess = response => ({
  type: actionTypes.UPDATE_ACTION_DESCRIPTION_SUCCESS,
  response,
  payload: {
    operation: operations.UPDATE
  }
});

export const setActionDescriptionError = (error, response) => ({
  type: actionTypes.UPDATE_ACTION_DESCRIPTION_ERROR,
  error,
  response,
  payload: {
    operation: operations.UPDATE
  }
});

export const setActionDescription =
  (actionId, description?) => async (dispatch, getState) => {
    const skillId = getActionSkillId(getState());
    const existingAction = getActionSelector(getState(), actionId);

    existingAction.loading = true;
    dispatch(
      setActionDescriptionStart(normalize(existingAction, actionSchema))
    );

    try {
      let updatedAction = existingAction;

      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with only selectIsNewIa on call to updateAction
      const useNewIAUpdatedAccess =
        selectIsNewIa(getState()) && selectIsAccessUpdatesEnabled(getState());
      const assistantId = getAssistantId(getState());
      updatedAction = await actionsAPI.updateAction(
        skillId,
        actionId,
        {
          action: actionId,
          description: description
        },
        { include_audit: true },
        useNewIAUpdatedAccess,
        assistantId
      );
      updatedAction = {
        ...existingAction,
        ...updatedAction,
        loading: false
      };

      dispatch(
        setActionDescriptionSuccess(normalize(updatedAction, actionSchema))
      );
    } catch (error) {
      const message = getErrorString(error);
      dispatch(
        messagesActions.addMessage({
          message,
          status: ToastStatus.ERROR,
          transactionId: error.transactionId
        })
      );
      existingAction.loading = false;
      dispatch(
        setActionDescriptionError(
          error,
          normalize(existingAction, actionSchema)
        )
      );
      throw error;
    }
  };

// -- updateActionDisambiguation ---
export const updateActionDisambiguationStart = response => ({
  type: actionTypes.UPDATE_ACTION_DISAMBIGUATION_START,
  response,
  payload: {
    operation: operations.UPDATE
  }
});

export const updateActionDisambiguationSuccess = response => ({
  type: actionTypes.UPDATE_ACTION_DISAMBIGUATION_SUCCESS,
  response,
  payload: {
    operation: operations.UPDATE
  }
});

export const updateActionDisambiguationError = (error, response) => ({
  type: actionTypes.UPDATE_ACTION_DISAMBIGUATION_ERROR,
  error,
  response,
  payload: {
    operation: operations.UPDATE
  }
});

export const updateActionDisambiguation =
  (skillId, actionId, disambiguationEnabled?) => async (dispatch, getState) => {
    const existingAction = getActionSelector(getState(), actionId);
    existingAction.loading = true;
    dispatch(
      updateActionDisambiguationStart(normalize(existingAction, actionSchema))
    );
    try {
      let updatedAction = existingAction;
      const disambiguation = { disambiguation_opt_out: disambiguationEnabled };
      if (existingAction.newAction) {
        updatedAction = {
          ...existingAction,
          ...disambiguation,
          updated: new Date().toISOString()
        };
      } else {
        // Use revised API for Access Control improvements if enabled & isNewIA
        // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with only selectIsNewIa on call to updateAction
        const useNewIAUpdatedAccess =
          selectIsNewIa(getState()) && selectIsAccessUpdatesEnabled(getState());
        const assistantId = getAssistantId(getState());
        updatedAction = await actionsAPI.updateAction(
          skillId,
          actionId,
          {
            action: actionId,
            disambiguation_opt_out: disambiguationEnabled
          },
          { include_audit: true },
          useNewIAUpdatedAccess,
          assistantId
        );
        updatedAction = {
          ...existingAction,
          ...updatedAction,
          ...disambiguation,
          loading: false
        };
      }

      dispatch(
        updateActionDisambiguationSuccess(
          normalize(updatedAction, actionSchema)
        )
      );
    } catch (error) {
      const message = getErrorString(error);
      dispatch(
        messagesActions.addMessage({
          message,
          status: ToastStatus.ERROR,
          transactionId: error.transactionId
        })
      );
      existingAction.loading = false;
      dispatch(
        updateActionDisambiguationError(
          error,
          normalize(existingAction, actionSchema)
        )
      );
      throw error;
    }
  };

// -- updateActionTopicSwitch ---
export const updateActionTopicSwitchStart = response => ({
  response,
  type: actionTypes.UPDATE_ACTION_TOPIC_SWITCH_START,
  payload: {
    operation: operations.UPDATE
  }
});

export const updateActionTopicSwitchSuccess = response => ({
  type: actionTypes.UPDATE_ACTION_TOPIC_SWITCH_SUCCESS,
  response,
  payload: {
    operation: operations.UPDATE
  }
});

export const updateActionTopicSwitchError = (error, response) => ({
  type: actionTypes.UPDATE_ACTION_TOPIC_SWITCH_ERROR,
  error,
  response,
  payload: {
    operation: operations.UPDATE
  }
});

export const updateActionTopicSwitch =
  (skillId, actionId, topicSwitchEnabled, neverReturnEnabled) =>
  async (dispatch, getState) => {
    const existingAction = getActionSelector(getState(), actionId);
    existingAction.loading = true;
    dispatch(
      updateActionTopicSwitchStart(normalize(existingAction, actionSchema))
    );
    try {
      const topicSwitch = {
        topic_switch: {
          allowed_from: topicSwitchEnabled,
          allowed_into: topicSwitchEnabled,
          never_return: neverReturnEnabled
        }
      };
      let updatedAction = existingAction;
      if (existingAction.newAction) {
        updatedAction = {
          ...existingAction,
          ...topicSwitch,
          updated: new Date().toISOString()
        };
      } else {
        // Use revised API for Access Control improvements if enabled & isNewIA
        // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with only selectIsNewIa on call to updateAction
        const useNewIAUpdatedAccess =
          selectIsNewIa(getState()) && selectIsAccessUpdatesEnabled(getState());
        const assistantId = getAssistantId(getState());
        const updatedActionMetadata = await actionsAPI.updateAction(
          skillId,
          actionId,
          {
            action: actionId,
            ...topicSwitch
          },
          { include_audit: true },
          useNewIAUpdatedAccess,
          assistantId
        );
        updatedAction = {
          ...existingAction,
          ...updatedActionMetadata,
          ...topicSwitch,
          loading: false
        };
      }

      dispatch(
        updateActionTopicSwitchSuccess(normalize(updatedAction, actionSchema))
      );
    } catch (error) {
      const message = getErrorString(error);
      dispatch(
        messagesActions.addMessage({
          message,
          status: ToastStatus.ERROR,
          transactionId: error.transactionId
        })
      );
      existingAction.loading = false;
      dispatch(
        updateActionTopicSwitchError(
          error,
          normalize(existingAction, actionSchema)
        )
      );
      throw error;
    }
  };

// --- renameAction ---

export const renameActionStart = response => {
  return {
    type: actionTypes.RENAME_ACTION_START,
    response,
    payload: {
      operation: operations.UPDATE
    }
  };
};

export const renameActionSuccess = response => {
  return {
    type: actionTypes.RENAME_ACTION_SUCCESS,
    response,
    payload: {
      operation: operations.UPDATE
    }
  };
};

export const renameActionError = (error, response) => ({
  type: actionTypes.RENAME_ACTION_ERROR,
  error,
  response,
  payload: {
    operation: operations.UPDATE
  }
});

export const renameAction =
  (skillId, newAction) => async (dispatch, getState) => {
    const existingAction = getActionSelector(getState(), newAction.action);
    const oldTitle = existingAction.title;
    existingAction.loading = true;
    dispatch(renameActionStart(normalize(existingAction, actionSchema)));
    try {
      // We must sanitize the title to prevent XSS attacks
      if (newAction?.title) {
        newAction.title = dompurify.sanitize(newAction.title);
      }
      // get action first
      const { action, title } = newAction;
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with only selectIsNewIa on call to updateAction
      const useNewIAUpdatedAccess =
        selectIsNewIa(getState()) && selectIsAccessUpdatesEnabled(getState());
      const assistantId = getAssistantId(getState());
      let updatedAction = await actionsAPI.updateAction(
        skillId,
        action,
        newAction,
        { action, title },
        useNewIAUpdatedAccess,
        assistantId
      );
      updatedAction = {
        ...existingAction,
        ...updatedAction,
        updated: new Date().toISOString(),
        loading: false
      };
      dispatch(renameActionSuccess(normalize(updatedAction, actionSchema)));
      if (oldTitle && oldTitle !== title) {
        updateWebChatHomeScreenButtons(getState(), dispatch, title, oldTitle);
      }
    } catch (error) {
      const message = getErrorString(error);
      dispatch(
        messagesActions.addMessage({
          message,
          status: ToastStatus.ERROR,
          transactionId: error.transactionId
        })
      );
      existingAction.loading = false;
      dispatch(
        renameActionError(error, normalize(existingAction, actionSchema))
      );
      throw error;
    }
  };

// --- update current action ---

export const loadCurrentAction = currentAction => ({
  type: actionTypes.LOAD_CURRENT_ACTION,
  currentAction
});

export const updateCurrentVarEntity = currentVarEntity => ({
  type: actionTypes.UPDATE_CURRENT_VAR_ENTITY,
  currentVarEntity
});

export const setFreeTextResponse = (
  stepId,
  deleteResponse,
  changeResponse,
  actionId,
  language,
  infoGatheringEnabled
) => ({
  type: modelsActionTypes.STEP_SET_FREE_TEXT_RESPONSE,
  stepId,
  deleteResponse,
  changeResponse,
  actionId,
  language,
  infoGatheringEnabled
});

export const defineCustomerResponse =
  (actionId, step, entityType, changeResponse?) => (dispatch, getState) => {
    const stepId = step.step;
    let entityId;
    let referenced;
    // the entityType can be a data_type

    if (changeResponse) {
      const dataTypeId = _.get(step, 'question.data_type');
      entityId = _.get(step, 'question.entity');
      dispatch({
        type: modelsActionTypes.STEP_REMOVE_CUSTOMER_RESPONSE,
        actionId,
        stepId,
        entityId,
        dataTypeId
      });

      const outputGeneric = _.get(step, 'output.generic');
      _.remove(
        outputGeneric,
        output =>
          (output as any).response_type ===
          actionUtil.RESPONSE_TYPES.response_from_data_type
      );
      _.set(step, 'output.generic', outputGeneric);
    }

    if (entityType) {
      const dataType = selectCustomDataTypeById(getState(), entityType);
      const language = selectSkillLanguage(getState());
      const launchModeSettings = selectLaunchModeSettings(getState());

      if (dataType) {
        const newStepOutput = _.cloneDeep(step.output) || {};
        if (!newStepOutput.generic) {
          newStepOutput.generic = [];
        }
        newStepOutput.generic.push({
          response_type: actionUtil.RESPONSE_TYPES.response_from_data_type,
          repeat_on_reprompt: true
        });
        const newStep = { ...step, output: newStepOutput };
        dispatch({
          type: modelsActionTypes.STEP_DEFINE_CUSTOMER_RESPONSE,
          payload: {
            operation: operations.UPDATE,
            response: { entities: { steps: { [stepId]: newStep } } }
          },
          stepId,
          dataTypeId: dataType.data_type,
          entityId: dataType.entity,
          entityExists: true,
          language,
          launchModeSettings
        });
      } else {
        entityId = entityType;
        // can be 'sys-number' or 'options' or 'regex' — if options we need to generate an id
        referenced = actionUtil.isSystemEntity(entityType);

        if (!referenced) {
          //not a system entity, generate a new id
          entityId = actionUtil.generateEntityName();
          const entityIds = Object.keys(getState().models.entities);
          while (entityIds.indexOf(entityId) !== -1) {
            entityId = actionUtil.generateEntityName();
          }
        } else {
          if (entityId === actionUtil.ENTITY_TYPES.confirmation.id) {
            dispatch({
              type: modelsActionTypes.STEP_ADD_YES_NO_OPTIONS,
              language,
              entityId,
              stepId
            });
          }

          if (entityId === actionUtil.ENTITY_TYPES.date.id) {
            dispatch({
              type: modelsActionTypes.STEP_ADD_DATEPICKER,
              stepId,
              entityId
            });
          }
        }

        let payload;
        let entityExists = true;

        if (!referenced || !getState().models.entities[entityId]) {
          let values = [];
          if (entityId === actionUtil.ENTITY_TYPES.confirmation.id) {
            values = actionUtil.getYesNoTranslationForLang(language);
          }
          entityExists = false;
          payload = actionUtil.assembleEntityPayload(
            operations.CREATE,
            entityId,
            {
              entity: entityId,
              fuzzy_match: !referenced ? true : undefined,
              type: referenced
                ? 'system'
                : entityType === actionUtil.ENTITY_TYPES.regex.id
                ? 'patterns'
                : undefined,
              values
            }
          );
        }
        if (actionUtil.ENTITIES_REQUIRE_NUMBER.includes(entityId)) {
          if (!getState().models.entities[actionUtil.ENTITY_TYPES.number.id]) {
            //If the step is using sys-currency/percentage but we do NOT have sys-number
            //enabled, then we need to make a specific call to add sys-number for the cases where
            //we prompt for "what percentage" and the user says "50" instead of "50%" or "forty five percent" etc.
            const numberPayload = actionUtil.assembleEntityPayload(
              operations.CREATE,
              actionUtil.ENTITY_TYPES.number.id,
              {
                entity: actionUtil.ENTITY_TYPES.number.id,
                type: 'system',
                values: []
              }
            );
            payload = _.merge(payload, numberPayload);
            dispatch({ type: actionTypes.ADD_SYS_NUMBER_AS_SUPPORTING_ENTITY });
          }
        }
        if (step.variable) {
          const dataType = actionUtil.getDataTypeByEntity(entityId, false);
          _.set(
            payload,
            `response.entities.action_variables.${step.variable}`,
            {
              data_type: dataType,
              title: step.variableTitle,
              variable: step.variable
            }
          );
        }

        dispatch({
          type: modelsActionTypes.STEP_DEFINE_CUSTOMER_RESPONSE,
          payload,
          stepId,
          entityId,
          entityExists,
          language,
          launchModeSettings
        });
      }
    } else {
      entityId = _.get(step, 'question.entity');
      const dataTypeId = _.get(step, 'question.data_type');
      if (dataTypeId === null) {
        return dispatch({
          type: modelsActionTypes.STEP_REMOVE_CUSTOMER_RESPONSE,
          actionId,
          stepId,
          entityId,
          dataTypeId
        });
      }
      if (!entityId && !dataTypeId) {
        if (actionUtil.hasDynamicOptions(step.question)) {
          return dispatch({
            type: modelsActionTypes.STEP_REMOVE_CUSTOMER_RESPONSE,
            actionId,
            stepId
          });
        }
        return;
      }
      if (dataTypeId) {
        // check if the data_type is referenced by another step.
        referenced = getCurrentAction(getState()).steps?.some(
          step =>
            step.step !== stepId && step.question?.data_type === dataTypeId
        );
      } else {
        referenced = actionUtil.isSystemEntity(entityId);
      }
      let payload;

      if (!referenced) {
        if (dataTypeId) {
          // check if the data type is newly created
          if (
            _.get(getState(), 'actions.dataTypes.create', []).includes(
              dataTypeId
            )
          ) {
            const cachedDataType = selectCustomDataTypeById(
              getState(),
              dataTypeId
            );
            entityId = cachedDataType?.entity;
            dispatch(removeDataType(dataTypeId));
          }
        } else {
          payload = actionUtil.assembleEntityPayload(
            operations.DELETE,
            entityId,
            {}
          );
        }
      }

      const entity = _.get(getState(), `models.entities[${entityId}]`);

      dispatch({
        type: modelsActionTypes.STEP_REMOVE_CUSTOMER_RESPONSE,
        payload,
        actionId,
        stepId,
        dataTypeId,
        entityId,
        entity,
        entityReferenced: referenced
      });
    }
    return entityId;
  };

// --- unloadActions --
export const unloadActionData = () => ({
  type: actionTypes.RESET_ACTIONS_STATE,
  payload: {
    operation: operations.RESET,
    response: {
      entities: {
        actions: {},
        entities: {},
        intents: {},
        steps: {},
        variables: {}
      }
    }
  }
});

export const updateReferencingConditions = (
  stepId,
  oldEntityId,
  newEntityId,
  variableId
) => ({
  type: modelsActionTypes.STEP_UPDATE_REFERENCED_ENTITY,
  payload: { stepId, oldEntityId, newEntityId, variableId }
});
// --- updateStepOptions ---

export const updateStepOptions =
  (actionId, step, options, optionsDiffMap, dataTypeTitle) =>
  (dispatch, getState) => {
    const dataTypeId = _.get(step, 'question.data_type');
    const dataType = selectCustomDataTypeById(getState(), dataTypeId);
    const entityId = dataType?.entity ?? _.get(step, 'question.entity', null);
    const stepId = step.step;
    const entity = _.get(getState(), `models.entities[${entityId}]`);
    let newDataTypeId = dataTypeId;
    let newEntityId;
    let dataTypeOperation;

    if (dataTypeTitle) {
      dataTypeOperation = operations.UPDATE;
      const patternEntity = actionUtil.isPatternsEntity(entity);
      if (!newDataTypeId) {
        // generate new data type id
        const dataTypeIds = selectCustomDataTypeIds(getState());
        newDataTypeId = actionUtil.generateDataTypeId();
        while (dataTypeIds.includes(newDataTypeId)) {
          newDataTypeId = actionUtil.generateDataTypeId();
        }
        dataTypeOperation = operations.CREATE;
      }
      // create/update the data type
      const newDataType = {
        id: newDataTypeId,
        data_type: newDataTypeId,
        entity: entityId,
        num_values: options?.length || 0,
        response_type: patternEntity
          ? actionUtil.DATA_TYPE_RESPONSE_TYPES.regex.id
          : actionUtil.DATA_TYPE_RESPONSE_TYPES.option.id,
        response: patternEntity
          ? undefined
          : {
              response_type: actionUtil.DATA_TYPE_RESPONSE_TYPES.option.id,
              options: actionUtil.buildOptionsList(options)
            },
        title: dataTypeTitle
      };
      dispatch(saveDataType(newDataType));
    } else if (dataTypeId) {
      // when we don't have data type title but have an associated data type
      // it means we should unlink the saved response
      // we need to create a new entity as well.
      newEntityId = dispatch(
        defineCustomerResponse(
          actionId,
          step,
          dataType?.response_type ?? actionUtil.ENTITY_TYPES.option.id
        )
      );
      // we also need to update the conditions to use the new entity id
      dispatch(
        updateReferencingConditions(
          stepId,
          entityId,
          newEntityId,
          step.variable
        )
      );
    }
    const finalEntityId = newEntityId ?? entityId;
    const values = [];
    const valuesToUpdate = [];
    const valuesToCreate = [];
    const valuesToDelete = [];
    options.forEach(option => {
      const { originalValue } = option;
      if (
        optionsDiffMap[originalValue] &&
        !optionsDiffMap[originalValue].delete
      ) {
        //user modified an existing value
        if (originalValue !== option.value) {
          // the user has changed the actual 'value'
          const tmp = _.find(options, { originalValue: option.value });
          if (tmp) {
            //the user has changed the value to match the 'originalValue' of another
            //value object
            values.push({
              ...option,
              originalValue: tmp.originalValue
            });
            valuesToUpdate.push(tmp.originalValue);
            return;
          }
        }
        valuesToUpdate.push(originalValue);
      } else if (!originalValue) {
        //in this case the option does not have an originalValue yet, so we will create a value with this value
        valuesToCreate.push(option.value);
        values.push({ ...option, originalValue: option.value });
        return;
      }
      values.push(option);
    });

    Object.keys(optionsDiffMap)
      .filter(key => optionsDiffMap[key].delete === true)
      .forEach(value =>
        valuesToDelete.push(optionsDiffMap[value].originalValue)
      );

    const response = {
      entities: {
        entities: {
          [finalEntityId]: {
            entity: finalEntityId,
            fuzzy_match: true,
            values
          }
        }
      }
    };
    dispatch({
      type: modelsActionTypes.STEP_UPDATE_OPTIONS,
      stepId,
      dataTypeOperation,
      dataTypeId: dataTypeTitle ? newDataTypeId : undefined,
      entity,
      entityId: finalEntityId,
      options,
      optionsDiffMap,
      payload: {
        operation: operations.UPDATE,
        response
      },
      valuesToCreate,
      valuesToDelete,
      valuesToUpdate
    });
  };

// --- updateDirtyState ---
export const updateDirtyState = isDirty => ({
  type: actionTypes.UPDATE_DIRTY_STATE,
  isDirty
});

export const updateStepValidation = (
  stepId,
  {
    numberOfTurns,
    respondWith,
    useLaunchModeDefault,
    inputValidationHandler,
    repeatOnReprompt
  }: {
    numberOfTurns: number;
    respondWith: string;
    useLaunchModeDefault?: boolean;
    inputValidationHandler: InputValidationHandler;
    repeatOnReprompt?: boolean;
  }
) => ({
  type: modelsActionTypes.STEP_UPDATE_VALIDATION,
  stepId,
  numberOfTurns,
  respondWith,
  useLaunchModeDefault,
  inputValidationHandler,
  repeatOnReprompt
});

export const updateExampleText = (actionId, exampleId, newText) => {
  const response = {
    entities: { examples: { [exampleId]: { text: newText } } }
  };
  return {
    actionId,
    type: modelsActionTypes.ACTIONS_UPDATE_EXAMPLE,
    response,
    payload: { operation: operations.UPDATE }
  };
};

export const addNewExample = (actionId, newExampleText) => {
  const response = {
    entities: {
      examples: { [newExampleText]: { text: newExampleText } }
    }
  };
  return {
    actionId,
    type: modelsActionTypes.ACTIONS_ADD_EXAMPLE,
    newExampleText,
    response,
    payload: { operation: operations.CREATE }
  };
};

export const addDisplayName = (actionId, displayName) => ({
  type: modelsActionTypes.ACTIONS_ADD_DISPLAY_NAME,
  actionId,
  displayName
});

export const addMultipleNewExamples = (actionId, newExamplesTexts) => {
  const examples = {};
  newExamplesTexts.forEach(newExampleText => {
    examples[newExampleText] = { text: newExampleText };
  });
  const response = { entities: { examples } };
  return {
    actionId,
    type: modelsActionTypes.ACTIONS_ADD_MULTIPLE_EXAMPLES,
    newExamplesTexts,
    response,
    payload: { operation: operations.CREATE }
  };
};

export const addMultipleNewCounterExamples = (actionId, newExamplesTexts) => {
  const examples = {};
  newExamplesTexts.forEach(newExampleText => {
    examples[newExampleText] = { text: newExampleText };
  });
  const response = { entities: { counterExamples: examples } };
  return {
    actionId,
    type: modelsActionTypes.ACTIONS_ADD_MULTIPLE_COUNTER_EXAMPLES,
    newExamplesTexts,
    response,
    payload: { operation: operations.CREATE }
  };
};

export const deleteExample = (actionId, exampleId) => {
  const response = {
    entities: { examples: { [exampleId]: {} } }
  };
  return {
    actionId,
    type: modelsActionTypes.ACTIONS_DELETE_EXAMPLE,
    exampleId,
    response,
    payload: { operation: operations.DELETE }
  };
};

export const getCounterExamplesSuccess = response => {
  return {
    type: actionTypes.GET_COUNTER_EXAMPLE_SUCCESS,
    counterExamples: Object.keys(response.entities.counterExamples ?? {}),
    payload: {
      operation: operations.GET,
      response
    }
  };
};

export const getCounterExamples = skillId => async (dispatch, getState) => {
  // Use revised API for Access Control improvements if enabled & isNewIA
  // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listCounterExamples
  const assistantId = getAssistantId(getState());
  const isNewIA = selectIsNewIa(getState());
  const useNewIAUpdatedAccess =
    isNewIA && selectIsAccessUpdatesEnabled(getState());
  const counterExampleApiResponse = await counterExampleApi.listCounterExamples(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  if (counterExampleApiResponse.counterexamples) {
    const response = normalize(
      counterExampleApiResponse.counterexamples,
      counterExamplesSchema
    );
    dispatch(getCounterExamplesSuccess(response));
  }
  /*
     else { should dispatch getCounterExamplesError }
     might want to extract the above into a stand alone function
  */
};

export const createCounterExample = newExampleText => {
  const response = {
    entities: {
      counterExamples: {
        [newExampleText]: { text: newExampleText }
      }
    }
  };
  return {
    type: actionTypes.CREATE_COUNTER_EXAMPLE,
    response,
    payload: { operation: operations.CREATE }
  };
};

export const updateCounterExample = (exampleId, newText) => {
  const response = {
    entities: {
      counterExamples: {
        [exampleId]: { text: newText }
      }
    }
  };
  return {
    type: actionTypes.UPDATE_COUNTER_EXAMPLE,
    response,
    payload: { operation: operations.UPDATE }
  };
};

export const deleteCounterExample = exampleId => {
  const response = {
    entities: { counterExamples: { [exampleId]: {} } }
  };
  return {
    type: actionTypes.DELETE_COUNTER_EXAMPLE,
    response,
    payload: { operation: operations.DELETE }
  };
};

export const selectStep = stepId => ({
  type: actionTypes.SELECT_STEP,
  stepId
});

interface NewStep {
  variableTitle: string;
  invalid_references: {
    callout: number;
    entity: number;
    intent: number;
    jump_to_step: number;
    value: number;
    variable: number;
  };
  resolver: {
    type: string;
    invoke_action?: {
      result_variable: string;
    };
    callout?: {
      result_variable: string;
    };
    client_actions?: [
      {
        result_variable: string;
      }
    ];
  };
  newStep: true;
  id: string;
  step: string;
  variable: string;
  question?: {
    data_type: string;
    entity: string;
  };
  isPrivateVariable?: boolean;
}

export const createStep = (
  actionId: string,
  orderedSteps: Step[],
  newStepIndex: number,
  autoSaveEnabled?: boolean,
  stepProps: { step?: string; next_step?: string; resolver?: any } = {},
  options = { select: true, duplicateStep: false },
  isPrivateVariable?: boolean
) => {
  let newStepId = actionUtil.generateStepId();

  while (orderedSteps.some(s => s.step === newStepId)) {
    newStepId = actionUtil.generateStepId();
  }

  const newStep: NewStep = {
    variableTitle: 'No response',
    invalid_references: {
      callout: 0,
      entity: 0,
      intent: 0,
      jump_to_step: 0,
      value: 0,
      variable: 0
    },
    resolver: {
      type: ResolverType.CONTINUE
    },
    ...stepProps,
    newStep: true,
    id: newStepId,
    step: newStepId,
    variable: newStepId
  };
  const newVariable = {
    title: newStep.variableTitle,
    variable: newStep.variable,
    data_type:
      newStep.question?.data_type ||
      actionUtil.getDataTypeByEntity(newStep.question?.entity, false),
    ...(isPrivateVariable && {
      privacy: {
        enabled: isPrivateVariable
      },
      examples: []
    })
  };

  const invokeActionResultVariable =
    newStep.resolver?.invoke_action?.result_variable;
  if (invokeActionResultVariable) {
    newStep.resolver.invoke_action.result_variable =
      invokeActionResultVariable.replace(stepProps.step, newStepId);
  }
  const calloutResultVariable = newStep.resolver?.callout?.result_variable;
  if (calloutResultVariable) {
    newStep.resolver.callout.result_variable = calloutResultVariable.replace(
      stepProps.step,
      newStepId
    );
  }

  if (
    newStep.resolver?.client_actions &&
    newStep.resolver?.client_actions?.length > 0
  ) {
    newStep.resolver.client_actions.forEach(clientAction => {
      if (clientAction.result_variable) {
        clientAction.result_variable = clientAction.result_variable.replace(
          stepProps.step,
          newStepId
        );
      }
    });
  }

  const payload = {
    operation: operations.CREATE,
    response: {
      entities: {
        steps: {
          [newStepId]: newStep
        },
        action_variables: {
          [newStep.variable]: newVariable
        }
      }
    },
    autoSaveEnabled: autoSaveEnabled
  };

  return {
    type: modelsActionTypes.STEP_CREATE,
    actionId,
    newStepIndex,
    stepId: newStepId,
    payload,
    newStep,
    options
  };
};

const duplicateStepEntity = sourceEntityId => (dispatch, getState) => {
  const sourceEntity = selectEntityById(getState(), sourceEntityId);
  let newEntityId = actionUtil.generateEntityName();
  const entityIds = Object.keys(getState().models.entities);
  while (entityIds.indexOf(newEntityId) !== -1) {
    newEntityId = actionUtil.generateEntityName();
  }
  const payload = actionUtil.assembleEntityPayload(
    operations.CREATE,
    newEntityId,
    {
      ...sourceEntity,
      entity: newEntityId
    }
  );
  dispatch({
    type: actionTypes.DUPLICATE_STEP_ENTITY,
    entityId: newEntityId,
    payload
  });
  return newEntityId;
};

const updateTriggerEntity =
  (entityName, words = [], defaultName, actionId) =>
  async (dispatch, getState) => {
    let entityNameToUpdate = entityName;

    if (!entityNameToUpdate) {
      const entityIds = getOrderedIds(getState());
      entityNameToUpdate = actionUtil.generateUniqueEntityName(
        defaultName,
        entityIds
      );
    }

    let operation = operations.UPDATE;
    let entity = getById(getState(), entityNameToUpdate);

    if (!entity) {
      operation = operations.CREATE;

      entity = {
        entity: entityNameToUpdate,
        fuzzy_match: false
      } as unknown as Entity;
    } else {
      entity = { ...entity };
    }

    entity.values = words.map(value => ({
      value,
      type: 'synonyms',
      synonyms: []
    }));

    dispatch({
      type: actionId,
      payload: {
        operation,
        response: {
          entities: {
            entities: {
              [entityNameToUpdate]: entity
            }
          }
        }
      }
    });
  };

export const setStepTitle = (stepId, title) => ({
  type: modelsActionTypes.STEP_SET_TITLE,
  stepId,
  title
});

export const setStepText = (stepId, text, defaultValue?) => ({
  type: modelsActionTypes.STEP_SET_TEXT,
  stepId,
  text,
  defaultValue
});

export const setStepMaxHits = (stepId, maxHits) => ({
  type: modelsActionTypes.SET_STEP_MAX_HITS,
  stepId,
  maxHits
});

export const setProfaneWordValues = (entityName, words) =>
  updateTriggerEntity(
    entityName,
    words,
    'profane_words',
    actionTypes.SET_PROFANE_WORD_VALUES
  );

export const setDangerWordValues = (entityName, words) =>
  updateTriggerEntity(
    entityName,
    words,
    'danger_words',
    actionTypes.SET_DANGER_WORD_VALUES
  );

export const onOutputUpdate = (stepId, jsonData) => ({
  type: modelsActionTypes.STEP_UPDATE_OUTPUT,
  stepId,
  jsonData
});

export const deleteStep = (actionId, step, orderedSteps) => {
  const entityId = _.get(step, 'question.entity', null);
  const stepId = _.get(step, 'step', null);
  const resultVariableId = getResultVariableId(step.resolver);

  const variableId = step.variable;
  let nextStepId = null;
  if (orderedSteps.length > 1) {
    const index = orderedSteps.findIndex(({ step }) => step === stepId);
    if (index === orderedSteps.length - 1) {
      nextStepId = orderedSteps[orderedSteps.length - 2]?.step;
    } else {
      nextStepId = orderedSteps[index + 1]?.step;
    }
  }
  const action = {
    type: modelsActionTypes.STEP_DELETE,
    actionId,
    entityId,
    nextStepId,
    stepId,
    variableId,
    resultVariableId,
    payload: {
      operation: operations.DELETE,
      response: {
        entities: {
          steps: {
            [stepId]: {}
          },
          action_variables: {
            [variableId]: {},
            [resultVariableId]: {}
          }
        }
      }
    }
  };
  if (entityId && !actionUtil.isSystemEntity(entityId)) {
    _.set(action, 'payload.response.entities.entities', { [entityId]: {} });
  }

  // track successful step deletion
  deletedObjectEvents.step({
    object: `${actionId}--${stepId}`,
    resultValue: 'success',
    successFlag: true
  });

  return action;
};

export const setStepCondition = (stepId, condition) => ({
  type: modelsActionTypes.STEP_UPDATE_CONDITION,
  condition,
  stepId
});

export const setActionCondition = (actionId, condition) => ({
  type: modelsActionTypes.ACTION_UPDATE_CONDITION,
  condition,
  actionId,
  isDirty: true
});

export const setEditorContext = editorContext => ({
  type: actionTypes.SET_EDITOR_CONTEXT,
  editorContext
});

export const reorderSteps = (actionId, sourceIndex, destinationIndex) => {
  ranProcessEvents.stepReordered(
    {
      category: 'actions-edit',
      resultValue: 'success',
      successFlag: true
    },
    {
      previousStepPosition: sourceIndex,
      newStepPosition: destinationIndex
    }
  );
  return {
    type: modelsActionTypes.STEP_REORDER,
    actionId,
    sourceIndex,
    destinationIndex
  };
};

export const setEndStepBehavior =
  (actionId, stepId, resolverType, updatedResolver) =>
  async (dispatch, getState) => {
    const existingStep = getStepById(getState(), stepId);
    const lastSavedSteps = _.get(getState(), `models.storeSteps`);
    const lastSavedStep = _.get(lastSavedSteps, stepId, null);
    const lastSavedResultVariable = getResultVariableId(
      lastSavedStep?.resolver
    );

    let deletedResultVariable = null;
    const existingResolver: string = _.get(existingStep, 'resolver.type');
    const currentResultVariable = getResultVariableId(existingStep?.resolver);
    let deleteResultVariable = true;
    if (
      (existingResolver === ResolverType.INVOKE_ANOTHER_ACTION &&
        resolverType === ResolverType.INVOKE_ANOTHER_ACTION_AND_END) ||
      (existingResolver === ResolverType.INVOKE_ANOTHER_ACTION_AND_END &&
        resolverType === ResolverType.INVOKE_ANOTHER_ACTION) ||
      existingResolver === resolverType ||
      resolverType === ResolverType.SEARCH
    ) {
      // if the resolver does not change or
      // if resolver type is search or
      // if switching from 'invoke_another_action' to 'invoke_another_action_and_end' or vice versa we do not want to delete
      // the result variable. In that scenario keep the existing result variable. However, in all other situations where there is a
      // result variable we should delete the result variable
      deleteResultVariable = false;
    }

    //Check if the resultVariable is in the step from store to be able to delete the result variable
    if (
      existingStep &&
      deleteResultVariable &&
      lastSavedResultVariable === currentResultVariable
    ) {
      deletedResultVariable = currentResultVariable;
    }
    return dispatch({
      type: modelsActionTypes.STEP_UPDATE_END_BEHAVIOR,
      actionId,
      deletedResultVariable,
      stepId,
      resolverType,
      updatedResolver
    });
  };

export const globalSettingsRedirectStart = tabName => ({
  type: actionTypes.GLOBAL_SETTINGS_REDIRECT_START,
  tabName
});

export const globalSettingsRedirectSuccess = () => ({
  type: actionTypes.GLOBAL_SETTINGS_REDIRECT_SUCCESS
});

export const setActionTitle = (actionId, title) => ({
  type: modelsActionTypes.ACTIONS_SET_TITLE,
  actionId,
  title
});

export const updateStepContext = (stepId, context) => ({
  type: modelsActionTypes.STEP_UPDATE_CONTEXT,
  context,
  stepId
});

export const setAutoSaveState = enabled => ({
  type: actionTypes.UPDATE_AUTO_SAVE,
  enabled
});

export const setPopulateWhenPrompted = (
  stepId,
  responseCollectionBehavior
) => ({
  type: modelsActionTypes.STEP_UPDATE_RESPONSE_COLLECTION_BEHAVIOR,
  stepId,
  responseCollectionBehavior
});

export const setPrivacyOnStep = createAction<{
  stepId: string;
  enabled: boolean;
}>('actions/update-privacy');

export const setCollectVerbatimOnStep = createAction<{
  stepId: string;
  enabled: boolean;
}>('actions/update-collect-verbatim');

export const setSlotExamplesOnStep = createAction<{
  stepId: string;
  examples: SlotFillingExample[];
}>('actions/update-slot-filling-examples');

export const updateAllowTopicSwitch = createAction<{
  stepId: string;
  enabled: boolean;
}>('actions/update-allow-topic-switch');

export const updateStepDynamicOptions = createAction<{
  stepId: string;
  question: StepQuestion;
}>('actions/update-step-dynamic-options');

export const addResponseFromVariableResponse = createAction<{
  stepId: string;
  language: string;
  launchModeSettings: { enabled?: boolean };
}>('actions/add-response-from-variable-response');

export const setStepOptions = createAsyncThunk<
  void,
  {
    actionId: string;
    step: Step;
    options: any[];
    optionsDiffMap: any;
    dataTypeTitle?: string;
  },
  CreateAsyncThunk
>(
  'actions/set-step-options',
  (
    { actionId, step, options, optionsDiffMap, dataTypeTitle },
    { dispatch, getState }
  ) => {
    const stepId = step.step;
    const stepEntityType = getStepEntityType(getState(), stepId);
    let newStep = step;
    if (
      stepEntityType &&
      stepEntityType === actionUtil.ENTITY_TYPES.dynamic_option.id
    ) {
      // if the current response is a dynamic option response
      // we need to clean the response first.
      dispatch(defineCustomerResponse(actionId, newStep, null, false));
      // then add a new option response
      dispatch(
        defineCustomerResponse(
          actionId,
          newStep,
          actionUtil.ENTITY_TYPES.option.id,
          false
        )
      );
      newStep = getStepById(getState(), stepId);
    }
    dispatch(
      updateStepOptions(
        actionId,
        newStep,
        options,
        optionsDiffMap,
        dataTypeTitle
      )
    );
  }
);

export const setStepDynamicOptions = createAsyncThunk<
  void,
  { stepId: string; question: StepQuestion },
  CreateAsyncThunk
>(
  'actions/set-step-dynamic-options',
  ({ stepId, question }, { dispatch, getState }) => {
    const actionId = getCurrentActionId(getState());
    const step = getStepById(getState(), stepId);
    const stepEntityType = getStepEntityType(getState(), stepId);
    if (
      stepEntityType &&
      stepEntityType !== actionUtil.ENTITY_TYPES.dynamic_option.id
    ) {
      // if the current response is not a dynamic option response
      // we need to clean the response first.
      dispatch(defineCustomerResponse(actionId, step, null, false));
      // then add a new dynamic option response
      const language = selectSkillLanguage(getState()); //language is needed to get the appropriate validation messages
      const launchModeSettings = selectLaunchModeSettings(getState());
      dispatch(
        addResponseFromVariableResponse({
          stepId,
          language,
          launchModeSettings
        })
      );
    }
    dispatch(updateStepDynamicOptions({ stepId, question }));
  }
);

// -- for ai guided action

export const setKnowledgeContent = knowledgeContent => ({
  type: actionTypes.SET_KNOWLEDGE_CONTENT,
  knowledgeContent
});

export const setPromptInstruction = promptInstruction => ({
  type: actionTypes.SET_PROMPT_INSTRUCTION,
  promptInstruction
});

export const setModelSelection = modelName => ({
  type: actionTypes.SET_MODEL_SELECTION,
  modelName
});

export const setAIGuided = aiGuided => ({
  type: actionTypes.SET_AI_GUIDED,
  aiGuided
});

export const setAIGuidedActionState = () => ({
  type: actionTypes.SET_IS_AI_GUIDED_ACTION
});

/**
 * NOTE this action allows the currentActionId to be corrected to a new value
 * with no other side-effects: nothing is loaded or changed except the redux
 * field actionId in the `actions` slice
 * @param actionId the action-id to set
 * @returns
 */
export const setCurrentActionId = actionId => ({
  type: actionTypes.SET_CURRENT_ACTION_ID,
  actionId
});

// --- getAction ---

export const getActionStart = actionId => ({
  type: modelsActionTypes.ACTIONS_GET_START,
  actionId,
  payload: {
    operation: operations.RESET,
    response: {
      entities: {
        examples: {},
        counterExamples: {}
      }
    }
  }
});

export const getActionSuccess = (response, actionId) => ({
  type: actionTypes.GET_ACTION_SUCCESS,
  actionId,
  payload: {
    operation: operations.GET,
    response
  }
});

export const getActionError = error => ({
  type: actionTypes.GET_ACTION_ERROR,
  error
});

export const getAction = (skillId, actionId) => async (dispatch, getState) => {
  // German: This method should not receive the `skillId` since it can be different
  // than the one "actionId" expects. This happens in New IA because `skillId` changes
  // when the assistant changes and sometimes this method gets called before
  // the loaded actions are unloaded
  // Xiaoyan: This hack brings another problem when the user lands in actions page
  // from the task list, the skill id under actions redux state is not set.
  // Then once you switch between actions automatically by the debug experience,
  // it will not load the correct action and switch to it.
  // In order to avoid both the above and this problem, we need to check only when
  // there is a previous skill id different from the target skill id.
  const state = getState();
  if (state.actions?.skillId && skillId !== state.actions?.skillId) {
    return Promise.resolve({});
  }

  dispatch(getActionStart(actionId));
  try {
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to getAction
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const action = await actionsAPI.getAction(
      skillId,
      actionId,
      useNewIAUpdatedAccess,
      assistantId
    );
    if (action?.steps) {
      //the store does not guarantee the order of the steps in the array
      action.steps = actionUtil.sortSteps(action.steps);
    }
    if (action?.boosts) {
      //hydrate redux state with boost words attached to action.
      dispatch(addBoosts(action.boosts));
      delete action.boosts; //we do not want to store this in redux as the boosts are being managed separately.
    }

    if (action?.launch_mode) {
      dispatch(addLaunchMode(action.launch_mode));
    } else {
      dispatch(addLaunchMode(null));
    }

    if (action.type === 'ai_guided') {
      if (action?.generative_ai?.ai_guided) {
        const aiGuided = action?.generative_ai?.ai_guided;
        dispatch(setAIGuided(aiGuided));
      } else {
        // grantite is the default model
        const aiGuidedInitialState = {
          ai_guided: {
            content: '',
            model_id: 'ibm/granite-13b-chat-v2',
            prompt_instruction: ''
          }
        };
        dispatch(setAIGuided(aiGuidedInitialState));
      }
      // backend doesn't store steps array for ai guided action, so we just make a step on UI
      action.steps = AIGuidedActionStep;
    }

    if (Array.isArray(action?.sub_action_references)) {
      for (const subaction of action.sub_action_references) {
        subaction.steps = actionUtil.sortSteps(subaction.steps);
      }
    }

    const normalized = normalize(action, actionSchema);

    // Adding a key called storeSteps to hold to steps from store and are updated during a save to stay in sync with what is in store.
    // storeSteps is currently only used to determine if the resolver change and a result variable needs to be deleted.
    if (_.get(normalized, 'entities.steps')) {
      normalized.entities.storeSteps = { ...normalized.entities.steps };
    }

    const intentId =
      action.condition &&
      (action.condition.intent || action.condition?.and?.[0]?.intent);
    if (intentId) {
      const intent = await getIntent(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        intentId
      );
      const response = normalize(intent, intentSchema);
      dispatch(getRelatedIntentSuccess(response));
    } else if (
      action.condition &&
      action.condition.expression === 'anything_else'
    ) {
      dispatch(getCounterExamples(skillId));
    } else {
      dispatch(getRelatedIntentError());
    }

    dispatch(getActionSuccess(normalized, actionId));
  } catch (error) {
    const message = getErrorString(error);
    dispatch(
      messagesActions.addMessage({
        message,
        status: ToastStatus.ERROR,
        transactionId: error.transactionId
      })
    );
    dispatch(getActionError(error));
    throw error;
  }
};

export const getSubActionStart = () => ({
  type: actionTypes.GET_SUB_ACTION_START
});

export const getSubActionSuccess = response => ({
  type: actionTypes.GET_SUB_ACTION_SUCCESS,
  payload: {
    operation: operations.GET,
    response
  }
});

export const getSubActionError = () => ({
  type: actionTypes.GET_SUB_ACTION_ERROR
});

export const getSubAction = actionId => async (dispatch, getState) => {
  const action = _.get(getState(), `models.actions.${actionId}`);
  if (
    _.get(getState(), `models.sub_action_references.${actionId}`) &&
    action.type !== ActionType.CONVERSATIONAL_SKILL
  ) {
    return;
  }
  const skillId = getActionSkillId(getState());
  dispatch(getSubActionStart());
  try {
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to getAction
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const subAction = await actionsAPI.getAction(
      skillId,
      actionId,
      useNewIAUpdatedAccess,
      assistantId
    );
    if (subAction && subAction.steps) {
      //the store does not guarantee the order of the steps in the array
      subAction.steps = actionUtil.sortSteps(subAction.steps);
    }
    const normalized = normalize(subAction, subActionSchema);
    dispatch(getSubActionSuccess(normalized));
  } catch (error) {
    const message = getErrorString(error);
    dispatch(
      messagesActions.addMessage({
        message,
        status: ToastStatus.ERROR,
        transactionId: error.transactionId
      })
    );
    dispatch(getSubActionError());
    throw error;
  }
};

export const saveCurrentActionStart = () => ({
  // actionName, updated action object
  type: actionTypes.SAVE_CURRENT_ACTION_START
});

export const saveCurrentActionSuccess = (
  actionId,
  condition,
  updated?,
  entitiesToClear?
) => ({
  type: modelsActionTypes.ACTIONS_SAVE,
  actionId,
  condition,
  updated,
  entitiesToClear
});

export const saveCurrentActionError = error => ({
  type: actionTypes.SAVE_CURRENT_ACTION_ERROR,
  error
});

export const saveCurrentActionConflictError = error => ({
  type: actionTypes.SAVE_CURRENT_ACTION_CONFLICT_ERROR,
  error
});

export const clearCurrentActionConflictError = () => ({
  type: actionTypes.CLEAR_CURRENT_ACTION_CONFLICT_ERROR
});

export const clearCurrentActionStart = () => ({
  type: actionTypes.CLEAR_ACTION_START,
  response: {
    entities: { examples: {}, counterExamples: {}, steps: {} }
  },
  payload: { operation: operations.RESET }
});

export const clearCurrentActionSuccess = () => ({
  type: actionTypes.CLEAR_ACTION_SUCCESS
});

export const clearCurrentAction = () => async (dispatch, getState) => {
  dispatch(clearCurrentActionStart());
  try {
    const skillId = getActionSkillId(getState());
    await dispatch(removeAllBoosts());
    await dispatch(fetchIntents(skillId));
    await dispatch(getCounterExamples(skillId));
    await dispatch(listActions(skillId));
    await dispatch(listVariables(skillId));
    await dispatch(listDataTypes());
    await dispatch(fetchEntities(skillId, {}, true, true));
    dispatch(clearCurrentActionSuccess());
  } catch (error) {
    const message = getErrorString(error);
    dispatch(
      messagesActions.addMessage({
        message,
        status: ToastStatus.ERROR,
        transactionId: error.transactionId
      })
    );
    dispatch(saveCurrentActionError(error));
    return error;
  }
};

export const openConvoActionPanel = (returnRoute, actionId = '') => ({
  type: actionTypes.OPEN_CONVO_ACTION_PANEL,
  returnRoute,
  actionId
});

export const hideConvoActionPanel = () => ({
  type: actionTypes.HIDE_CONVO_ACTION_PANEL
});

export const clearConvoActionEditId = () => ({
  type: actionTypes.CLEAR_CONVO_ACTION_EDIT_ID
});

// --- updateAction ---

export const saveCurrentAction =
  (skillId, clearCurrentActionWhenComplete) => async (dispatch, getState) => {
    dispatch(saveCurrentActionStart());
    //the entire action and all associated artifacts are in redux state
    //use a selector to figure out what exactly needs to be saved
    const isNewIA = selectIsNewIa(getState());
    const entirePayload = assemblePayloadToSave(getState());
    const {
      action,
      dataTypes,
      entities,
      entitiesToReset,
      intent,
      values,
      variables,
      variablesToDelete
    } = entirePayload;
    const segmentEventsToTrigger = [];
    //provide an ordered list (array) of operations to the transactions API.
    //transactions api will create/update/delete based on the operations provided
    //operations occur in the order in which they are provided
    //the following is the order that operations should appear
    //1. save/update intents
    //OR
    //1. save/update counterExamples
    //2. save/update entities
    //3. create/update/delete entity values (of entities that have been 'updated'
    //4. save/update dataTypes
    //5. save/update global variables
    //6. save/update action
    //the transactions API enforces referential integrity. If an action referring to an intent/entity is updated/created
    //prior to the intent/entity being created then the overall transaction will fail.
    const transactionOperations: Transaction[] = [];
    try {
      if (_.get(action, 'condition.expression', null) !== 'anything_else') {
        if (intent) {
          transactionOperations.push({
            type: 'intent',
            operation: intent.operation,
            payload: intent,
            target_reference: intent.intent
          });
          if (intent.operation) {
            segmentEventsToTrigger.push({
              type: 'intent',
              id: intent.intent,
              operation: intent.operation
            });
          }
        }
      }
      if (
        entirePayload.counterExamples &&
        _.get(action, 'condition.expression', null) === 'anything_else'
      ) {
        transactionOperations.push({
          type: 'skill',
          operation: TransactionOperation.UPDATE,
          payload: {
            workspace: {
              counterexamples: entirePayload.counterExamples || []
            }
          },
          target_reference: skillId
        });
      }

      if (entities && entities.length > 0) {
        entities.forEach(entity => {
          transactionOperations.push({
            type: 'entity',
            operation: entity.operation,
            payload: entity,
            target_reference: entity.entity
          });
          if (entity.operation) {
            segmentEventsToTrigger.push({
              type: 'entity',
              id: entity.entity,
              operation: entity.operation
            });
          }
        });
      }

      if (values && values.length > 0) {
        values.forEach(value => {
          transactionOperations.push({
            operation: value.operation,
            parent_references: { entity: value.entity },
            payload: value,
            target_reference: value.originalValue ?? value.value,
            type: 'value'
          });
        });
      }

      if (dataTypes && dataTypes.length > 0) {
        dataTypes.forEach(dataType => {
          transactionOperations.push({
            type: 'data_type',
            operation: dataType.operation,
            payload: dataType,
            target_reference: dataType.data_type
          });
          if (dataType.operation) {
            segmentEventsToTrigger.push({
              type: 'data_type',
              id: dataType.data_type,
              operation: dataType.operation
            });
          }
        });
      }

      if (entirePayload.globalVariables) {
        const existingGlobalVariables = getVariables(getState());
        entirePayload.globalVariables.forEach(variable => {
          if (existingGlobalVariables[variable.variable]?.updated) {
            transactionOperations.push({
              type: 'variable',
              operation: TransactionOperation.UPDATE,
              payload: variable,
              target_reference: variable.variable
            });
            segmentEventsToTrigger.push({
              type: 'variable',
              id: variable.variable,
              operation: 'update'
            });
          } else {
            transactionOperations.push({
              type: 'variable',
              operation: TransactionOperation.CREATE,
              payload: variable
            });
            segmentEventsToTrigger.push({
              type: 'variable',
              id: variable.variable,
              operation: 'create'
            });
          }
        });
      }

      const options = {
        if_not_modified_since: action.updated
      };

      if (variables && variables.length > 0) {
        //In normal transaction API calls we need to send the variables separate to
        //the action content.
        //The reason for this is because when we POST a full
        //action object we are telling the store to completely replace the action.

        //To do this the store deletes the existing action contents, in the process
        //deleting all variables of an action, thus deleting all references to that
        //variable. This presents an issue as it will cause the action to get into
        //an invalid state simply due to the way the UPDATE/POST is processed.

        //To work around this constraint the store allows us to save variables separate from
        //the main action. This allows us to first update the action, and subsequently
        //update/delete the variables modified/deleted.

        //In the 'create' case this is problematic, as it is not possible to update the variables
        //prior to the action being created - in the create [action] case we simply append the
        //variables array to the action and update as normal.
        //For 'update' cases we add another operation to the transaction API to update the variables.
        if (action.operation === operations.CREATE) {
          action.variables = variables;
        } else {
          const vars: Transaction = {
            type: 'variable',
            operation: TransactionOperation.UPSERT,
            parent_references: {
              action: action.action
            },
            payload: {
              variables: _.uniqBy(variables, 'variable')
            }
          };
          transactionOperations.push(vars);
        }
      }

      // We must sanitize the title to prevent XSS attacks
      if (action?.title) {
        action.title = dompurify.sanitize(action.title);
      }
      if (action?.tempTitle) {
        action.tempTitle = dompurify.sanitize(action.tempTitle);
      }

      if (action && action.operation && action.action) {
        transactionOperations.push({
          type: 'action',
          operation: action.operation,
          payload: action,
          target_reference: action.action,
          options
        });
      }

      if (
        variablesToDelete &&
        action.operation !== TransactionOperation.DELETE
      ) {
        variablesToDelete.forEach(varId => {
          transactionOperations.push({
            type: 'variable',
            operation: TransactionOperation.DELETE,
            target_reference: varId,
            parent_references: {
              action: action.action
            }
          });
        });
      }

      if (action.operation) {
        // on custom action create, there is no type yet. Therefore need to set actionType to standard
        segmentEventsToTrigger.push({
          type: 'action',
          id: action.action,
          operation: action.operation,
          actionType: action?.type || ActionSkillTypes.STANDARD
        });

        if (action?.operation === TransactionOperation.UPDATE) {
          action?.steps?.forEach(step => {
            if (step?.resolver?.invoke_action?.policy === 'dynamic') {
              segmentEventsToTrigger.push({
                type: 'dynamicSubaction',
                id: step.step,
                operation: TransactionOperation.UPDATE
              });
            }
          });
        }
      }

      // If the fallback_connect_to_agent intent is ever missing in the fallback action, for whatever reason, place it
      // back to prevent the fallback action from being missed.
      if (
        action.action === 'fallback' &&
        !_.get(action, 'condition.intent', null)
      ) {
        _.set(action, 'condition.intent', 'fallback_connect_to_agent');
      }

      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to sendTransactions
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(getState());
      const assistantId = getAssistantId(getState());

      // This is to used to strip out empty steps array from the transaction payload
      const transactionOperationsUpdated = transactionOperations.map(
        transaction => {
          if (
            transaction.operation !== TransactionOperation.DELETE &&
            transaction?.payload?.steps &&
            transaction?.payload?.steps.length === 0
          ) {
            delete transaction?.payload?.steps;
            return transaction;
          } else {
            return transaction;
          }
        }
      );

      //operations are assembled - call transactions api
      const transactionApiResponse = await sendTransactions(
        skillId,
        {
          operations: transactionOperationsUpdated
        },
        useNewIAUpdatedAccess,
        assistantId
      );

      if (action.operation === TransactionOperation.CREATE) {
        updateWebChatHomeScreenButtons(getState(), dispatch, action.title);
      } else if (action.operation === TransactionOperation.UPDATE) {
        const oldTitle = _.get(
          getState(),
          `models.actions.${action.action}.originalTitle`
        );
        if (oldTitle && oldTitle !== action.title) {
          updateWebChatHomeScreenButtons(
            getState(),
            dispatch,
            action.title,
            oldTitle
          );
        }
      }
      //condition may have been updated in the selector above, need to pass in the updated condition
      await dispatch(
        saveCurrentActionSuccess(
          action.action,
          action.condition,
          (transactionApiResponse as any).updated,
          _.uniq(entitiesToReset || [])
        )
      );

      if (isNewIA) {
        // OnboardingTaskList: Mark 'Create your first action' task complete
        // Only triggers when creating an action as opposed to every save
        if (action.operation === TransactionOperation.CREATE) {
          await dispatch(
            updateTaskCompletionStatus(
              TaskListKey.HAS_CREATED_FIRST_ACTION,
              true
            )
          );
        }
        if (action?.steps?.length > 1) {
          // OnboardingTaskList: Mark 'Create steps' task complete
          // Each action comes with 1 default step, if the length is greater than 1 it means they added their own step
          await dispatch(
            updateTaskCompletionStatus(TaskListKey.HAS_CREATED_STEPS, true)
          );
        }

        // OnboardingTaskList: Mark 'Add 5 examples' task complete when at least five examples are detected on the assistant
        if (intent?.examples?.length >= 5) {
          await dispatch(
            updateTaskCompletionStatus(
              TaskListKey.HAS_ADDED_FIVE_EXAMPLES,
              true
            )
          );
        }
      }

      await getSkillStatus(skillId, useNewIAUpdatedAccess, assistantId);
      dispatch(pollSkillTrainingStatus());
      if (clearCurrentActionWhenComplete) {
        dispatch(clearCurrentAction());
      }
      if (segmentEventsToTrigger && segmentEventsToTrigger.length > 0) {
        segmentEventsToTrigger.forEach(event => {
          switch (event.operation) {
            case 'create':
              triggerCreateObjectEvent(event, skillId);
              break;
            case 'update': // when update operations are instrumented add call here.
              triggerUpdateObjectEvent(event, skillId);
              break;
            case 'delete': // when delete operations are instrumented add call here.
              triggerDeleteObjectEvent(event, skillId);
              break;
            default:
              break;
          }
        });
      }
    } catch (error) {
      console.error(error);

      if (error.code && error.code === 409) {
        dispatch(saveCurrentActionConflictError(error));
      } else {
        const message = getErrorString(error);
        dispatch(
          messagesActions.addMessage({
            message,
            status: ToastStatus.ERROR,
            transactionId: error.transactionId
          })
        );
        dispatch(saveCurrentActionError(error));
      }
      return error;
    }
  };

export const duplicateStep = sourceStepId => (dispatch, getState) => {
  const actionId = getCurrentActionId(getState());
  const orderedSteps = getOrderedSteps(getState());
  const autoSaveEnabled = isAutoSaveEnabled(getState());
  const sourceStepIndex = orderedSteps.findIndex(
    ({ step }) => step === sourceStepId
  ); // this should always exist (> -1)
  const newStep = _.cloneDeep(orderedSteps[sourceStepIndex]);
  // when a step has an entity associated
  // we need to duplicate the entity as well
  if (
    newStep.question?.entity &&
    !actionUtil.isSystemEntity(newStep.question.entity)
  ) {
    newStep.question.entity = dispatch(
      duplicateStepEntity(newStep.question.entity)
    );
  }

  const isPrivateFeatureEnabled = isActionsPrivacyEnabled(getState());
  //check if the step that is being duplicated has private variable
  const isPrivateVariable = isStepVariablePrivate(getState(), sourceStepId);
  dispatch(
    createStep(
      actionId,
      orderedSteps,
      sourceStepIndex + 1,
      autoSaveEnabled,
      newStep,
      { select: true, duplicateStep: true },
      isPrivateFeatureEnabled ? isPrivateVariable : false // If the feature is enabled use isPrivateVariable otherwise it is false
    )
  );
  const skillId = getActionSkillId(getState());
  dispatch(saveCurrentAction(skillId, false));
};

// --- createAction ---

export const createEmptyAction = () => (dispatch, getState) => {
  dispatch(clearCurrentActionStart());
  dispatch(clearCurrentActionSuccess());
  const stepId = actionUtil.generateStepId();
  let actionId = actionUtil.generateActionId();
  const { actions } = getState().models;
  while (Object.keys(actions).includes(actionId)) {
    actionId = actionUtil.generateActionId();
  }
  const launchModeSettings = selectLaunchModeSettings(getState()) as LaunchMode;

  const intentId = actionUtil.generateActionIntentName(actionId);
  const date = new Date().toISOString();
  const newAction: Omit<Action, 'title' | 'steps'> &
    Partial<Pick<Action, 'steps'>> = {
    action: actionId,
    id: actionId, //needed for data tables
    condition: { intent: intentId },
    //this is a little hacky, there are situations where the list of actions is not retrieved before the user clicks on the "create action" button
    //in that situation we do NOT want the 'next_action' to be undefined. If it is undefined store will create the action _after_ the "anything_else" action
    //making the newly created action unreachable. As a workaround, if we don't find the welcome action in the list of actions we will tell store to insert the newly
    //created action before the "fallback" action
    next_action: actions?.welcome?.next_action ?? 'fallback',
    newAction: true,
    created: date,
    updated: date,
    disambiguation_opt_out: false, // if disambiguation is true, we want want opt_out to be false,
    topic_switch: {
      allowed_from: true,
      allowed_into: true,
      never_return: false
    },
    launch_mode: launchModeSettings?.default
  };

  const firstStep = {
    invalid_references: {
      callout: 0,
      entity: 0,
      intent: 0,
      jump_to_step: 0,
      value: 0,
      variable: 0
    },
    step: stepId,
    variableTitle: 'No response',
    variable: stepId
  };
  newAction.steps = [stepId] as unknown as Step[]; //when normalized action object just has array of step id for steps
  dispatch({
    type: actionTypes.CREATE_EMPTY_ACTION_START,
    payload: {
      operation: operations.RESET,
      response: {
        entities: {
          steps: {} //reset steps model to be empty, new action being loaded
        }
      }
    }
  });
  const newVariable = {
    title: firstStep.variableTitle,
    variable: firstStep.variable,
    data_type: DATA_TYPE.ANY
  };
  dispatch({
    type: actionTypes.CREATE_EMPTY_ACTION,
    actionId,
    launchMode: launchModeSettings?.default,
    variableId: stepId,
    payload: {
      operation: operations.CREATE,
      response: {
        entities: {
          actions: {
            [actionId]: newAction
          },
          steps: {
            [stepId]: firstStep
          },
          action_variables: {
            [firstStep.variable]: newVariable
          }
        }
      }
    }
  });
  return newAction;
};

export const createActionError = error => ({
  type: actionTypes.CREATE_ACTION_ERROR,
  error
});

export const createNewActionFromExamples =
  (actionName: string, examples: string[]) => async (dispatch, getState) => {
    try {
      const skillId = getActionSkillId(getState());
      await dispatch(createEmptyAction());
      const actionId = getState().actions.actionId;
      await dispatch(setActionTitle(actionId, actionName));
      await dispatch(addMultipleNewExamples(actionId, examples));
      await dispatch(saveCurrentAction(skillId, true));
      return actionId;
    } catch (error) {
      const message = getErrorString(error);
      dispatch(
        messagesActions.addMessage({
          message,
          status: ToastStatus.ERROR,
          transactionId: error.transactionId
        })
      );
      return error;
    }
  };
